import { useState } from "react";
import {
  collection,
  onSnapshot,
  getDocs,
} from "firebase/firestore";
import { db } from "../../../../../config/firebaseConfig";

// History cut-off: only show movements from this date onwards
const HISTORY_START = new Date("2026-03-01T00:00:00");

// Parse a date that may be ISO string, JS Date, or "dd-mm-yyyy" / "dd/mm/yyyy"
function parseMovementDate(timestamp, dateStr) {
  if (dateStr && typeof dateStr === "string") {
    const m = dateStr.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
    if (m) {
      const [, dd, mm, yyyy] = m;
      return new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    }
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) return d;
  }
  if (timestamp) {
    const d = new Date(timestamp);
    if (!isNaN(d.getTime())) return d;
  }
  return null;
}

function isAfterCutoff(timestamp, dateStr) {
  const d = parseMovementDate(timestamp, dateStr);
  if (!d) return false; // exclude undated entries
  return d.getTime() >= HISTORY_START.getTime();
}

function compareMovementsByDateDesc(a, b) {
  const dateA = parseMovementDate(a.timestamp, a.date)?.getTime() ?? 0;
  const dateB = parseMovementDate(b.timestamp, b.date)?.getTime() ?? 0;

  if (dateA !== dateB) return dateB - dateA;

  const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
  const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
  return timeB - timeA;
}

/**
 * Custom hook to fetch station history (livraisons and ventes)
 */
export function useStationHistory(produit) {
  const [selectedStation, setSelectedStation] = useState(null);
  const [stationHistory, setStationHistory] = useState({
    movements: [],
    loading: false,
  });
  const [allStationsHistory, setAllStationsHistory] = useState({
    movements: [],
    loading: false,
  });

  const fetchStationHistory = (station) => {
    setSelectedStation(station);
    setStationHistory({ movements: [], loading: true });

    // Find user for this station
    const usersRef = collection(db, "users");
    onSnapshot(usersRef, (usersSnap) => {
      let userId = null;
      usersSnap.forEach((doc) => {
        const data = doc.data();
        // Skip test users (e.g. test_lumumba_pompist) so they never
        // shadow the real station user when matching by station.
        if (doc.id.startsWith("test_")) return;
        if (data.station && data.station.toLowerCase() === station.id) {
          userId = doc.id;
        }
      });

      if (!userId) {
        setStationHistory({ movements: [], loading: false });
        return;
      }

      // Fetch all rapport entries; date filtering/limiting happens below.
      const rapportRef = collection(db, "users", userId, "rapport");

      onSnapshot(rapportRef, (rapportSnap) => {
        const ventes = [];
        rapportSnap.forEach((doc) => {
          const data = doc.data();
          const isEssence = produit === "essence";
          const volume = isEssence ? data.ST || 0 : data.GT || 0;

          if (
            volume > 0 &&
            isAfterCutoff(data.timestamp, data.date || doc.id)
          ) {
            ventes.push({
              date: data.date || doc.id,
              volume: volume,
              timestamp: data.timestamp,
            });
          }
        });

        // Fetch all APPROV entries; date filtering/limiting happens below.
        const dataRef = collection(db, "users", userId, "data");

        onSnapshot(dataRef, (dataSnap) => {
          const movements = [];

          // Add livraisons
          dataSnap.forEach((doc) => {
            const data = doc.data();
            Object.entries(data).forEach(([key, value]) => {
              if (key.startsWith("APPROV") && value) {
                const isEssence = produit === "essence";
                const isMatchingProduct =
                  (isEssence && value.produit === "SU") ||
                  (!isEssence && value.produit === "GO");

                if (isMatchingProduct && value.qte) {
                  const qte =
                    parseFloat(String(value.qte).replace(",", ".")) || 0;
                  if (
                    qte > 0 &&
                    isAfterCutoff(value.timestamp, value.date || doc.id)
                  ) {
                    // Handle multiple citernes (cuve1, cuve2)
                    const citernes = [];
                    if (value.cuve1) citernes.push(value.cuve1);
                    if (value.cuve2) citernes.push(value.cuve2);

                    const cuveLabel =
                      citernes.length > 0 ? citernes.join(" + ") : "Citerne";

                    movements.push({
                      type: "livraison",
                      date: value.date || doc.id,
                      volume: qte * 1000, // Convert m³ to liters
                      cuve: cuveLabel,
                      numbl: value.numbl,
                      timestamp: value.timestamp,
                    });
                  }
                }
              }
            });
          });

          // Add ventes
          ventes.forEach((vente) => {
            movements.push({
              type: "vente",
              date: vente.date,
              volume: vente.volume,
              timestamp: vente.timestamp,
            });
          });

          // Sort all movements by their event date, newest first.
          movements.sort(compareMovementsByDateDesc);

          setStationHistory({
            movements, // Filtrage par mois délégué au composant appelant
            loading: false,
          });
        });
      });
    });
  };

  const fetchAllStationsHistory = async (stations) => {
    console.log(
      "[useStationHistory] fetchAllStationsHistory called with",
      stations.length,
      "stations",
    );
    setAllStationsHistory({ movements: [], loading: true });

    // Fetch all movements and limit to last 30
    const allMovements = [];

    // Fetch history for each station
    for (const station of stations) {
      console.log("[useStationHistory] Processing station:", station.id);
      // Find user for this station
      const usersSnap = await getDocs(collection(db, "users"));
      let userId = null;
      usersSnap.forEach((doc) => {
        const data = doc.data();
        // Skip test users so they never shadow the real station user.
        if (doc.id.startsWith("test_")) return;
        if (data.station && data.station.toLowerCase() === station.id) {
          userId = doc.id;
        }
      });

      if (!userId) continue;

      // Fetch rapport (ventes)
      const rapportRef = collection(db, "users", userId, "rapport");
      const rapportSnap = await getDocs(rapportRef);

      rapportSnap.forEach((doc) => {
        const data = doc.data();

        const isEssence = produit === "essence";
        const volume = isEssence ? data.ST || 0 : data.GT || 0;

        if (volume > 0 && isAfterCutoff(data.timestamp, data.date || doc.id)) {
          allMovements.push({
            station: station.label,
            type: "vente",
            date: data.date || doc.id,
            volume: volume,
            timestamp: data.timestamp,
          });
        }
      });

      // Fetch data for APPROV (livraisons)
      const dataRef = collection(db, "users", userId, "data");
      const dataSnap = await getDocs(dataRef);

      dataSnap.forEach((doc) => {
        const data = doc.data();
        Object.entries(data).forEach(([key, value]) => {
          if (key.startsWith("APPROV") && value) {
            const isEssence = produit === "essence";
            const isMatchingProduct =
              (isEssence && value.produit === "SU") ||
              (!isEssence && value.produit === "GO");

            if (isMatchingProduct && value.qte) {
              const qte = parseFloat(String(value.qte).replace(",", ".")) || 0;
              if (
                qte > 0 &&
                isAfterCutoff(value.timestamp, value.date || doc.id)
              ) {
                const citernes = [];
                if (value.cuve1) citernes.push(value.cuve1);
                if (value.cuve2) citernes.push(value.cuve2);

                const cuveLabel =
                  citernes.length > 0 ? citernes.join(" + ") : "Citerne";

                allMovements.push({
                  station: station.label,
                  type: "livraison",
                  date: value.date || doc.id,
                  volume: qte * 1000,
                  cuve: cuveLabel,
                  numbl: value.numbl,
                  timestamp: value.timestamp,
                });
              }
            }
          }
        });
      });
    }

    // Sort by event date, newest first.
    allMovements.sort(compareMovementsByDateDesc);

    console.log(
      "[useStationHistory] Total movements loaded:",
      allMovements.length,
    );

    setAllStationsHistory({
      movements: allMovements, // Filtrage par mois délégué au composant appelant
      loading: false,
    });
  };

  const clearSelectedStation = () => {
    setSelectedStation(null);
  };

  return {
    selectedStation,
    stationHistory,
    fetchStationHistory,
    clearSelectedStation,
    allStationsHistory,
    fetchAllStationsHistory,
  };
}
